import { FC, useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { AuditTrailViewProps } from "./auditTrail.interfaces";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledDateField from "../../components/controlledComponents/ControlledDateField";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { useAuditTrailHook } from "../../../hooks/useAuditTrailHook";
import {
  TAuditTrailFilter,
  TConfigurationDomain,
  TAuditTrailEntry,
  TChangeDetail,
} from "../../../apis/auditTrail/auditTrail.interfaces";
import { useAuditTrailService } from "../../../apis/auditTrail/useAuditTrailService";
import dayjs from "dayjs";

type AuditTrailFormValues = {
  searchQuery: string;
  dateFrom: ReturnType<typeof dayjs> | null;
  dateTo: ReturnType<typeof dayjs> | null;
  selectedUser: string;
  selectedDomain: TConfigurationDomain;
};

const configurationDomains: TConfigurationDomain[] = [
  "all",
  "institute",
  "spocPremise",
  "compliance",
  "feeStructure",
  "academic",
  "assessment",
  "branding",
  "localization",
  "rbac",
];

const domainOptions = configurationDomains.map((domain) => ({
  key: domain,
  value:
    domain === "all"
      ? "All Domains"
      : domain.charAt(0).toUpperCase() + domain.slice(1),
}));

const AuditTrailView: FC<AuditTrailViewProps> = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { control, watch } = useForm<AuditTrailFormValues>({
    defaultValues: {
      searchQuery: "",
      dateFrom: null,
      dateTo: null,
      selectedUser: "",
      selectedDomain: "all",
    },
  });

  const searchQuery = watch("searchQuery");
  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");
  const selectedUser = watch("selectedUser");
  const selectedDomain = watch("selectedDomain");

  useEffect(() => {
    setPage(0);
  }, [searchQuery, dateFrom?.valueOf(), dateTo?.valueOf(), selectedUser, selectedDomain]);

  const filters: TAuditTrailFilter = useMemo(
    () => ({
      page: page + 1,
      pageSize,
      ...(searchQuery && { searchQuery }),
      ...(dateFrom && { dateFrom: dateFrom.toISOString() }),
      ...(dateTo && { dateTo: dateTo.toISOString() }),
      ...(selectedUser && { userId: selectedUser }),
      ...(selectedDomain !== "all" && { configurationDomain: selectedDomain }),
    }),
    [
      page,
      pageSize,
      searchQuery,
      dateFrom,
      dateTo,
      selectedUser,
      selectedDomain,
    ],
  );

  const { data, isLoading, error } = useAuditTrailHook(filters);
  const { exportAuditTrail } = useAuditTrailService();

  const handleExport = useCallback(async () => {
    try {
      const blob = await exportAuditTrail(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-trail-${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export audit trail:", err);
    }
  }, [exportAuditTrail, filters]);

  const formatChangeDetails = (changes: TChangeDetail[]): string => {
    return changes
      .map((change) => {
        const fieldName = change.displayName || change.field;
        if (change.oldValue === null) {
          return `${fieldName} set to "${change.newValue}"`;
        } else if (change.newValue === null) {
          return `${fieldName} removed (was "${change.oldValue}")`;
        } else {
          return `${fieldName} changed from "${change.oldValue}" to "${change.newValue}"`;
        }
      })
      .join("; ");
  };

  const getActionColor = (
    action: string,
  ): "success" | "error" | "warning" | "info" | "default" => {
    switch (action) {
      case "create":
        return "success";
      case "update":
        return "info";
      case "delete":
        return "error";
      case "archive":
        return "warning";
      case "restore":
        return "success";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.auditTrail} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="audit-trail-view"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.auditTrail} />
        <Box data-testid="audit-trail-view">
          <Alert severity="error">
            Failed to load audit trail data. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.auditTrail} />

      <Grid container spacing={3} data-testid="audit-trail-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            data-testid="audit-trail-header"
          >
            <MainTitle title="Audit Trail" />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              data-testid="export-audit-trail-button"
            >
              Export
            </Button>
          </Box>
        </Grid>

        {/* Filters */}
        <Grid item xs={12}>
          <Card data-testid="audit-trail-filters-card">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterListIcon />
                <Typography variant="h6">Filters</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <ControlledTextField
                    control={control as any}
                    name="searchQuery"
                    id="audit-trail-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by keywords..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "audit-trail-search-input" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <ControlledDateField
                    control={control as any}
                    name="dateFrom"
                    id="audit-trail-from-date"
                    label="From Date"
                    isGridItem={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        "data-testid": "audit-trail-from-date",
                      } as any,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <ControlledDateField
                    control={control as any}
                    name="dateTo"
                    id="audit-trail-to-date"
                    label="To Date"
                    isGridItem={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        "data-testid": "audit-trail-to-date",
                      } as any,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <ControlledSelectField
                    control={control as any}
                    name="selectedDomain"
                    id="audit-trail-domain-select"
                    label="Domain"
                    itemList={domainOptions}
                    isGridItem={false}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <ControlledTextField
                    control={control as any}
                    name="selectedUser"
                    id="audit-trail-user"
                    label="User ID"
                    isGridItem={false}
                    fullWidth
                    placeholder="Filter by user ID"
                    inputProps={{ "data-testid": "audit-trail-user-input" }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Audit Trail Table */}
        <Grid item xs={12}>
          <Card data-testid="audit-trail-table-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Audit Entries ({data?.totalCount || 0})
              </Typography>
              {!data?.entries || data.entries.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="audit-trail-empty-state"
                >
                  <HistoryIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Typography variant="h6" color="text.secondary">
                    No audit entries found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ||
                    dateFrom ||
                    dateTo ||
                    selectedUser ||
                    selectedDomain !== "all"
                      ? "Try adjusting your filters"
                      : "No configuration changes have been recorded yet"}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    data-testid="audit-trail-table-container"
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Action</TableCell>
                          <TableCell>Entity</TableCell>
                          <TableCell>Domain</TableCell>
                          <TableCell>Changes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.entries.map((entry: TAuditTrailEntry) => (
                          <TableRow
                            key={entry.id}
                            hover
                            data-testid={`audit-trail-row-${entry.id}`}
                          >
                            <TableCell>
                              {dayjs(entry.timestamp).format(
                                "YYYY-MM-DD HH:mm:ss",
                              )}
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {entry.userName}
                                </Typography>
                                {entry.userEmail && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {entry.userEmail}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={entry.action}
                                color={getActionColor(entry.action)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {entry.entityName || entry.entityId}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {entry.entityType}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={entry.configurationDomain || "N/A"}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 400 }}
                              >
                                {formatChangeDetails(entry.changeDetails)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={data?.totalCount || 0}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    data-testid="audit-trail-pagination"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default AuditTrailView;
